import React, {FormEvent, useEffect, useState} from 'react';

import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {Redirect, useLocation} from 'react-router-dom';

import {LanguageSelector} from '@/widgets/LanguageSelector';
import {ROUTES} from '@app/routing';
import {userModel} from '@entities/user/model';
import {useUserPermissions} from '@entities/user/permissions';
import {history} from '@shared/lib/history';
import {Button} from '@shared/ui/Button';
import {Checkbox} from '@shared/ui/Checkbox';
import {Hint} from '@shared/ui/Hint';
import {Input} from '@shared/ui/Input';
import {Link} from '@shared/ui/Link';

import {
    AuthContent,
    AuthDescription,
    AuthTitle,
    ButtonContainer,
    CheckboxLabel,
    Field,
    Fields,
    LanguageWrapper,
    MainContainer,
    ServiceTitle,
} from './styled';

export function LoginPage() {
    const {t} = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [saveCredentials, setSaveCredentials] = useState(true);

    const user = useUnit(userModel);
    const {defaultPrivateRoute} = useUserPermissions();

    useEffect(() => {
        if (user.state.authData) history.push(ROUTES.TWO_FA);
    }, [user.state.authData]);

    const {search} = useLocation();

    useEffect(() => {
        const query = new URLSearchParams(search);
        const token = query.get('token');
        if (token) {
            const {username, password} = JSON.parse(atob(token));
            if (username && password) {
                setUsername(username);
                setPassword(password);
                user.login({username, password});
            }
        }
    }, []);

    function onSubmit(e?: FormEvent) {
        e?.preventDefault();
        user.login({username, password});
    }

    if (user.state.isAuth) return <Redirect to={defaultPrivateRoute} />;

    return (
        <>
            <div>
                <ServiceTitle size={4}>traceCORE B2B E-Invoicing</ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <AuthContent>
                    <AuthTitle size={3}>{t('login.title')}</AuthTitle>
                    <form onSubmit={onSubmit}>
                        <AuthDescription>{t('login.description')}</AuthDescription>
                        <Hint isError={user.state.error.login !== null}>
                            <Fields>
                                <Field>
                                    <Input
                                        id="login"
                                        data-id="input-login"
                                        placeholder={t('login.loginPlaceholder')}
                                        autoComplete="off"
                                        disabled={user.state.pending}
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        hasError={user.state.error.login !== null}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        id="password"
                                        data-id="input-password"
                                        placeholder={t('login.passwordPlaceholder')}
                                        value={password}
                                        type="password"
                                        autoComplete="off"
                                        disabled={user.state.pending}
                                        onChange={(e) => setPassword(e.target.value)}
                                        hasError={user.state.error.login !== null}
                                    />
                                </Field>
                                <Field>
                                    <Checkbox
                                        id="remember-me"
                                        data-id="cb-remember"
                                        onChange={() => setSaveCredentials(!saveCredentials)}
                                        checked={saveCredentials}
                                    >
                                        <CheckboxLabel>{t('login.rememberMe')}</CheckboxLabel>
                                    </Checkbox>
                                </Field>
                                <Field>
                                    <Link to={ROUTES.REQUEST_RESET}>{t('login.forgot')}</Link>
                                </Field>
                            </Fields>
                        </Hint>
                        <ButtonContainer>
                            <Button
                                type={'submit'}
                                isLoading={user.state.pending}
                                data-id="btn-submit"
                            >
                                {t('login.submitButton')}
                            </Button>
                        </ButtonContainer>
                    </form>
                </AuthContent>
            </MainContainer>
        </>
    );
}
